import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  UserGroupIcon,
  ShareIcon,
  ChatBubbleLeftEllipsisIcon,
  BellIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  TrashIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import {
  getSharedWithMe,
  getSharedByMe,
  searchRecruiters,
  shareCandidate,
  unshareCandidate,
  getCandidateComments,
  addCandidateComment,
  deleteCandidateComment,
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  getUnreadCount
} from '../services/api';

function Collaboration() {
  const [activeTab, setActiveTab] = useState('shared-with-me');
  const [loading, setLoading] = useState(false);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [sharedByMe, setSharedByMe] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareCandidate_, setShareCandidate_] = useState(null);
  const [recruiterSearch, setRecruiterSearch] = useState('');
  const [recruiterResults, setRecruiterResults] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  const [sharing, setSharing] = useState(false);

  // Comments modal state
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [commentsCandidate, setCommentsCandidate] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);

  // Fetch shared with me
  const fetchSharedWithMe = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getSharedWithMe();
      setSharedWithMe(response.data.candidates || []);
    } catch (error) {
      console.error('Error fetching shared candidates:', error);
      toast.error('Failed to load shared candidates');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch shared by me
  const fetchSharedByMe = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getSharedByMe();
      setSharedByMe(response.data.candidates || []);
    } catch (error) {
      console.error('Error fetching candidates you shared:', error);
      toast.error('Failed to load your shared candidates');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await getNotifications({ limit: 50 });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await getUnreadCount();
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (activeTab === 'shared-with-me') {
      fetchSharedWithMe();
    } else if (activeTab === 'shared-by-me') {
      fetchSharedByMe();
    } else if (activeTab === 'notifications') {
      fetchNotifications();
    }
    fetchUnreadCount();
  }, [activeTab, fetchSharedWithMe, fetchSharedByMe, fetchNotifications, fetchUnreadCount]);

  // Search recruiters for sharing
  useEffect(() => {
    const searchTimer = setTimeout(async () => {
      if (recruiterSearch.length >= 2) {
        try {
          const response = await searchRecruiters(recruiterSearch);
          setRecruiterResults(response.data.recruiters || []);
        } catch (error) {
          console.error('Error searching recruiters:', error);
        }
      } else {
        setRecruiterResults([]);
      }
    }, 300);

    return () => clearTimeout(searchTimer);
  }, [recruiterSearch]);

  // Handle share
  const handleShare = async () => {
    if (!shareCandidate_ || !selectedRecruiter) {
      toast.error('Please select a recruiter to share with');
      return;
    }

    setSharing(true);
    try {
      await shareCandidate(shareCandidate_._id, selectedRecruiter.email, shareMessage);
      toast.success(`Shared ${shareCandidate_.fullName} with ${selectedRecruiter.name || selectedRecruiter.email}`);
      setShowShareModal(false);
      setShareCandidate_(null);
      setSelectedRecruiter(null);
      setShareMessage('');
      setRecruiterSearch('');
      fetchSharedByMe();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to share candidate');
    } finally {
      setSharing(false);
    }
  };

  // Handle unshare
  const handleUnshare = async (candidateId, recruiterId, recruiterEmail) => {
    if (!window.confirm(`Remove sharing with ${recruiterEmail}?`)) return;

    try {
      await unshareCandidate(candidateId, recruiterId);
      toast.success('Sharing removed');
      fetchSharedByMe();
    } catch (error) {
      toast.error('Failed to remove sharing');
    }
  };

  // Open comments modal
  const openComments = async (candidate) => {
    setCommentsCandidate(candidate);
    setShowCommentsModal(true);
    setLoadingComments(true);

    try {
      const response = await getCandidateComments(candidate._id);
      setComments(response.data.comments || []);
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  };

  // Add comment
  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setSendingComment(true);
    try {
      const response = await addCandidateComment(commentsCandidate._id, newComment);
      setComments([response.data.comment, ...comments]);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSendingComment(false);
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      await deleteCandidateComment(commentsCandidate._id, commentId);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  // Mark notification as read
  const handleMarkRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications(notifications.map(n =>
        n._id === notificationId ? { ...n, read: true } : n
      ));
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserGroupIcon className="h-7 w-7 text-indigo-600" />
            Collaboration
          </h1>
          <p className="text-gray-500 mt-1">Share candidates and collaborate with your team</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('shared-with-me')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'shared-with-me'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ShareIcon className="h-5 w-5" />
            Shared With Me
          </button>
          <button
            onClick={() => setActiveTab('shared-by-me')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'shared-by-me'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <UserGroupIcon className="h-5 w-5" />
            Shared By Me
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'notifications'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BellIcon className="h-5 w-5" />
            Notifications
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                {unreadCount}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : (
        <>
          {/* Shared With Me */}
          {activeTab === 'shared-with-me' && (
            <div className="bg-white rounded-lg border">
              {sharedWithMe.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <ShareIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No candidates shared with you yet</p>
                  <p className="mt-1">When other recruiters share candidates with you, they will appear here.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {sharedWithMe.map((candidate) => (
                    <div key={candidate._id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{candidate.fullName}</h3>
                        <p className="text-sm text-gray-500">{candidate.email}</p>
                        {candidate.sharedBy && (
                          <p className="text-xs text-indigo-600 mt-1">
                            Shared by: {candidate.sharedBy.name || candidate.sharedBy.email}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => openComments(candidate)}
                        className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-indigo-600 hover:bg-gray-100 rounded-lg"
                      >
                        <ChatBubbleLeftEllipsisIcon className="h-5 w-5" />
                        Comments
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Shared By Me */}
          {activeTab === 'shared-by-me' && (
            <div className="bg-white rounded-lg border">
              {sharedByMe.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <UserGroupIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">You haven't shared any candidates yet</p>
                  <p className="mt-1">Share candidates from the Talent Pool to collaborate with your team.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {sharedByMe.map((candidate) => (
                    <div key={candidate._id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900">{candidate.fullName}</h3>
                          <p className="text-sm text-gray-500">{candidate.email}</p>
                        </div>
                        <button
                          onClick={() => openComments(candidate)}
                          className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-indigo-600 hover:bg-gray-100 rounded-lg"
                        >
                          <ChatBubbleLeftEllipsisIcon className="h-5 w-5" />
                          Comments
                        </button>
                      </div>
                      {candidate.sharedWithRecruiters && candidate.sharedWithRecruiters.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {candidate.sharedWithRecruiters.map((recruiter) => (
                            <span
                              key={recruiter.id}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-sm rounded-full"
                            >
                              {recruiter.name || recruiter.email}
                              <button
                                onClick={() => handleUnshare(candidate._id, recruiter.id, recruiter.email)}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div className="bg-white rounded-lg border">
              {notifications.length > 0 && (
                <div className="p-3 border-b flex justify-end">
                  <button
                    onClick={handleMarkAllRead}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    Mark all as read
                  </button>
                </div>
              )}
              {notifications.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <BellIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No notifications</p>
                  <p className="mt-1">You're all caught up!</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {notifications.map((notification) => (
                    <div
                      key={notification._id}
                      className={`p-4 flex items-start gap-3 ${
                        !notification.read ? 'bg-indigo-50' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => !notification.read && handleMarkRead(notification._id)}
                    >
                      <div className={`p-2 rounded-full ${
                        notification.type === 'candidate_shared' ? 'bg-green-100' :
                        notification.type === 'comment_added' ? 'bg-blue-100' :
                        'bg-gray-100'
                      }`}>
                        {notification.type === 'candidate_shared' ? (
                          <ShareIcon className="h-5 w-5 text-green-600" />
                        ) : notification.type === 'comment_added' ? (
                          <ChatBubbleLeftEllipsisIcon className="h-5 w-5 text-blue-600" />
                        ) : (
                          <BellIcon className="h-5 w-5 text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{notification.title}</p>
                        <p className="text-sm text-gray-600">{notification.message}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatDate(notification.createdAt)}</p>
                      </div>
                      {!notification.read && (
                        <CheckCircleIcon className="h-5 w-5 text-indigo-600" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Share Modal */}
      {showShareModal && shareCandidate_ && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500/75" onClick={() => setShowShareModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Share Candidate</h3>
              <p className="text-sm text-gray-600 mb-4">
                Share <strong>{shareCandidate_.fullName}</strong> with another recruiter
              </p>

              {/* Recruiter search */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Recruiter
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={recruiterSearch}
                    onChange={(e) => setRecruiterSearch(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Search results */}
                {recruiterResults.length > 0 && (
                  <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto">
                    {recruiterResults.map((recruiter) => (
                      <button
                        key={recruiter.id}
                        onClick={() => {
                          setSelectedRecruiter(recruiter);
                          setRecruiterSearch(recruiter.email);
                          setRecruiterResults([]);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b last:border-b-0"
                      >
                        <p className="font-medium">{recruiter.name}</p>
                        <p className="text-sm text-gray-500">{recruiter.email}</p>
                      </button>
                    ))}
                  </div>
                )}

                {selectedRecruiter && (
                  <div className="mt-2 p-2 bg-indigo-50 rounded-lg flex items-center justify-between">
                    <span className="text-sm text-indigo-700">
                      {selectedRecruiter.name || selectedRecruiter.email}
                    </span>
                    <button
                      onClick={() => {
                        setSelectedRecruiter(null);
                        setRecruiterSearch('');
                      }}
                      className="text-indigo-500 hover:text-indigo-700"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Message */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message (optional)
                </label>
                <textarea
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                  placeholder="Add a note for the recruiter..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowShareModal(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShare}
                  disabled={!selectedRecruiter || sharing}
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sharing ? 'Sharing...' : 'Share'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {showCommentsModal && commentsCandidate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500/75" onClick={() => setShowCommentsModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
              {/* Header */}
              <div className="p-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Comments</h3>
                  <p className="text-sm text-gray-500">{commentsCandidate.fullName}</p>
                </div>
                <button
                  onClick={() => setShowCommentsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Comments list */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loadingComments ? (
                  <div className="flex justify-center py-8">
                    <ArrowPathIcon className="h-6 w-6 animate-spin text-indigo-600" />
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <ChatBubbleLeftEllipsisIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    <p>No comments yet</p>
                  </div>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {comment.author?.name || comment.author?.email}
                          </p>
                          <p className="text-xs text-gray-500">{formatDate(comment.createdAt)}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="mt-2 text-sm text-gray-700">{comment.content}</p>
                    </div>
                  ))
                )}
              </div>

              {/* Add comment */}
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || sendingComment}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <PaperAirplaneIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Collaboration;
